import {
  formatRecipientLabel,
  normalizeRecipientOption,
  uniqueRecipientsByKey,
} from '@/composables/people/recipientOptions';
import collaboratorsService from '@/services/collaborators';
import peopleService from '@/services/people';
import simpleNotesService from '@/services/simpleNotes';
import { SEND_EMAIL_TEXT } from '@/texts/catalog/sendEmail';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';
import { resolveText } from '@/utils/textValue';

const uniqueById = (items = []) => {
  const seen = new Set();
  const uniqueItems = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = resolveText(item?.id || item?.document_id || '', '');
    if (id === '' || seen.has(id)) {
      return;
    }

    seen.add(id);
    uniqueItems.push(item);
  });

  return uniqueItems;
};

const resolveSimpleNoteText = () => {
  const section = SEND_EMAIL_TEXT?.simpleNotes;
  return section && typeof section === 'object' ? section : {};
};

const normalizeRecipientsCollection = (collection = [], prefix = 'recipient') => {
  const normalized = (Array.isArray(collection) ? collection : [])
    .map((item, index) => normalizeRecipientOption(item, index, prefix))
    .filter(Boolean);

  return uniqueRecipientsByKey(normalized);
};

const resolveSimpleNoteContextData = (vm) => {
  const contextData = vm.contextData && typeof vm.contextData === 'object' ? vm.contextData : {};
  const simpleNoteData = contextData.simpleNoteData && typeof contextData.simpleNoteData === 'object'
    ? contextData.simpleNoteData
    : {};

  const simpleNoteId = Number(contextData.id || contextData.simpleNoteId || simpleNoteData.id || 0) || 0;
  const simpleNoteReference = resolveText(
    contextData.reference
      || `${resolveText(simpleNoteData.serial, 'NS')}${resolveText(simpleNoteData.folio, '')}`,
    simpleNoteId > 0 ? `#${simpleNoteId}` : ''
  );

  const customerName = resolveText(
    contextData.customerName
      || simpleNoteData.customer_name
      || simpleNoteData.company_name,
    ''
  );

  const total = Number(contextData.total ?? simpleNoteData.total ?? 0) || 0;
  const currency = resolveText(contextData.currency || simpleNoteData.currency, 'MXN');

  return {
    simpleNoteId,
    simpleNoteData,
    simpleNoteReference,
    customerName,
    total,
    currency,
  };
};

const resolveSimpleNoteOwnerId = (simpleNoteContext = {}) => {
  const simpleNoteData = simpleNoteContext.simpleNoteData && typeof simpleNoteContext.simpleNoteData === 'object'
    ? simpleNoteContext.simpleNoteData
    : {};

  return Number(simpleNoteData.customer_id || simpleNoteData.company_id || 0) || 0;
};

const resolveSimpleNotePrimaryRecipient = (simpleNoteContext = {}) => {
  const simpleNoteData = simpleNoteContext.simpleNoteData && typeof simpleNoteContext.simpleNoteData === 'object'
    ? simpleNoteContext.simpleNoteData
    : {};

  const name = resolveText(simpleNoteData.customer_name || simpleNoteData.company_name || simpleNoteContext.customerName, '');
  const email = resolveText(simpleNoteData.customer_email || simpleNoteData.email, '');

  return {
    id: resolveText(simpleNoteData.customer_id || simpleNoteData.company_id || '', ''),
    name,
    email,
    label: formatRecipientLabel(name, email),
  };
};

const resolveSimpleNoteFallbackRecipients = (simpleNoteContext = {}, primaryRecipient = {}) => {
  const candidates = [];

  if (primaryRecipient && typeof primaryRecipient === 'object') {
    candidates.push(primaryRecipient);
  }

  const simpleNoteData = simpleNoteContext.simpleNoteData && typeof simpleNoteContext.simpleNoteData === 'object'
    ? simpleNoteContext.simpleNoteData
    : {};

  [
    'persons',
    'people',
    'contacts',
    'contact_people',
    'copyRecipients',
    'ccRecipients',
    'sendCcOptions',
  ].forEach((key) => {
    const list = Array.isArray(simpleNoteData[key]) ? simpleNoteData[key] : [];
    list.forEach((item) => {
      candidates.push(item);
    });
  });

  return normalizeRecipientsCollection(candidates, 'fallback-recipient');
};

const buildSimpleNoteSummaryMeta = (simpleNoteContext = {}) => {
  const summaryText = resolveSimpleNoteText()?.summary || {};
  const sendTypeLabelValue = resolveText(summaryText?.sendTypeValue, 'Nota simple');
  const reference = resolveText(simpleNoteContext.simpleNoteReference, '');
  const sendTypeValue = `${sendTypeLabelValue}${reference !== '' ? ` ${reference}` : ''}`.trim();

  return {
    sendTypeLabel: resolveText(summaryText?.sendTypeLabel, ''),
    sendTypeValue,
    counterpartLabel: resolveText(summaryText?.customerLabel || summaryText?.counterpartLabel, ''),
    counterpartValue: resolveText(simpleNoteContext.customerName, ''),
    totalLabel: resolveText(summaryText?.totalLabel, ''),
    totalValue: formatCurrency(simpleNoteContext.total, { currency: simpleNoteContext.currency || 'MXN' }),
  };
};

const resolveInitialToRecipientIds = (vm) => {
  const options = Array.isArray(vm.normalizedToRecipientOptions) ? vm.normalizedToRecipientOptions : [];

  const preferredIds = options
    .filter((item) => {
      const invoiceMailing = Number(item?.invoice_mailing || 0) === 1;
      const paymentsMailing = Number(item?.payments_mailing || 0) === 1;
      return (invoiceMailing || paymentsMailing) && resolveText(item?.email, '') !== '';
    })
    .map((item) => item.id);

  if (preferredIds.length > 0) {
    return preferredIds;
  }

  const primaryId = resolveText(vm.normalizedPrimaryRecipient?.id, '');
  if (primaryId !== '') {
    return [primaryId];
  }

  const firstRecipient = options.find((item) => resolveText(item?.email, '') !== '');
  return firstRecipient ? [firstRecipient.id] : [];
};

const resolveInitialCcRecipientIds = (vm) => {
  const options = Array.isArray(vm.normalizedCcRecipientOptions) ? vm.normalizedCcRecipientOptions : [];

  return options
    .filter((item) => {
      const invoiceMailing = Number(item?.invoice_mailing || 0) === 1;
      const paymentsMailing = Number(item?.payments_mailing || 0) === 1;
      return (invoiceMailing || paymentsMailing) && resolveText(item?.email, '') !== '';
    })
    .map((item) => item.id);
};

const resolveDefaultMessage = (customerName = '') => {
  const defaultMessage = resolveSimpleNoteText()?.defaultMessage;
  if (typeof defaultMessage === 'function') {
    return String(defaultMessage(customerName || '') || '');
  }

  return resolveText(defaultMessage, '');
};

const hydrateSimpleNoteContextData = async (vm) => {
  const simpleNoteContext = resolveSimpleNoteContextData(vm);
  const ownerId = resolveSimpleNoteOwnerId(simpleNoteContext);
  const primaryRecipient = resolveSimpleNotePrimaryRecipient(simpleNoteContext);

  vm.text = resolveSimpleNoteText();
  vm.summaryMeta = buildSimpleNoteSummaryMeta(simpleNoteContext);
  vm.primaryRecipient = primaryRecipient;
  vm.messageType = 'simple_note';
  vm.messageTypeCandidates = ['simple_note'];

  const customerName = resolveText(simpleNoteContext.customerName, '').replace(/\s+/g, ' ').trim();
  vm.messageVariables = {
    customer: customerName,
    customerName,
    NOMBRE_CLIENTE: customerName,
  };
  vm.messageLoadingText = resolveText(vm.text?.messageLoadingText, '');
  vm.initialMessage = resolveDefaultMessage(customerName);
  vm.recipientsLoading = ownerId > 0;

  const [recipientsResult, copyResult, documentsResult] = await Promise.allSettled([
    ownerId > 0
      ? peopleService.selectors({ customer_id: ownerId, mailing_type: 'payments', only_enabled: 1 })
      : Promise.resolve([]),
    collaboratorsService.invoiceMailingSelectors(),
    simpleNoteContext.simpleNoteId > 0
      ? simpleNotesService.documentsIndex(simpleNoteContext.simpleNoteId)
      : Promise.resolve({}),
  ]);

  let recipientOptions = [];
  if (recipientsResult.status === 'fulfilled') {
    recipientOptions = normalizeRecipientsCollection(extractCollection(recipientsResult.value), 'person');
  }

  if (recipientOptions.length === 0) {
    recipientOptions = resolveSimpleNoteFallbackRecipients(simpleNoteContext, primaryRecipient);
  }

  let copyOptions = [];
  if (copyResult.status === 'fulfilled') {
    copyOptions = normalizeRecipientsCollection(extractCollection(copyResult.value), 'collaborator');
  }

  let serviceDocuments = [];
  if (documentsResult.status === 'fulfilled') {
    const payload = extractPayload(documentsResult.value);
    serviceDocuments = Array.isArray(payload?.documents) ? payload.documents : [];
  }

  vm.recipientOptions = recipientOptions;
  vm.copyOptions = copyOptions;
  vm.invoiceAttachmentOptions = [];
  vm.documentAttachmentOptions = uniqueById(serviceDocuments);
  vm.recipientsLoading = false;
  vm.initialToRecipientIds = resolveInitialToRecipientIds(vm);
  vm.initialCcRecipientIds = resolveInitialCcRecipientIds(vm);
};

export const sendEmailSimpleNoteContextConfig = Object.freeze({
  supportsInvoiceAttachments: true,
  allowInvoiceAttachmentSelection: false,
  resolveText() {
    return resolveSimpleNoteText();
  },
  hydrateContextData: hydrateSimpleNoteContextData,
  resolveSelectedInvoiceAttachmentItems() {
    return [];
  },
  resolveDefaultInvoiceAttachmentItem(vm) {
    const summaryValue = resolveText(vm.summaryData?.sendTypeValue, 'Nota simple');

    return {
      id: 'simple-note-default-files',
      label: `${summaryValue} (PDF)`.trim(),
      group: 'invoice-default',
      locked: true,
    };
  },
  resolveInvoiceAttachmentSummaryLabel(vm, option) {
    return String(option?.label || '').trim();
  },
});
